import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import ExclamationMark from '@/assets/icons/exclamation-mark-circle';
import { getGeocomplyClient } from '@/data/location/geocomply-common';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { GeoComplyError } from '@/feature/betslip-sbk/types';
import { useGeocomplyErrorProps } from '@/feature/game-details-arcade/hooks/use-geocomply-error';
import { logger } from '@/utils/logging';

const getGeotoken = async () => {
    const geocomplyClient = getGeocomplyClient();
    const { decoded } = await geocomplyClient.request({ readCache: true });
    return decoded;
};

const TOTAL_RETRIES = 3;

const LOG_TAG = '[Arcade Geotoken]';

// Prefetch geotoken for arcade lobby to avoid delay when user attempts to play the game
// 3 attempts to get the geotoken, if failed, show error sheet
export const usePrefetchGeotoken = (geocomplyClientReady: boolean) => {
    const { showInfoSheet } = useAlerts();
    const { t } = useTranslation();
    const { getGeocomplyErrorProps } = useGeocomplyErrorProps();
    const isMountedRef = useRef(true);

    const showErrorSheet = useCallback(
        (error?: unknown) => {
            let title = t('location_failure');
            let description: string | React.ReactNode = t('error_subtitle');

            if (error instanceof GeoComplyError) {
                const errorProps = getGeocomplyErrorProps(error);
                title = errorProps.title;
                description = errorProps.description;
            }

            if (!isMountedRef.current) {
                return;
            }

            showInfoSheet({
                title,
                description,
                buttonLabel: t('close'),
                icon: <ExclamationMark />,
            });
        },
        [showInfoSheet, t, getGeocomplyErrorProps]
    );

    const fetchGeotokenWithRetry = useCallback(async () => {
        for (let attempt = 1; attempt <= TOTAL_RETRIES; attempt++) {
            if (!isMountedRef.current) {
                return;
            }

            logger.debug(LOG_TAG, 'Prefetch geotoken attempt', attempt);
            try {
                const decoded = await getGeotoken();

                // If we get a successful response and can bet, we're done
                if (decoded.can_bet) {
                    logger.debug(LOG_TAG, 'Prefetch geotoken successfully');
                    return;
                }

                // If we can't bet and this is the last attempt, show error
                if (attempt === TOTAL_RETRIES && decoded) {
                    showErrorSheet(new GeoComplyError(decoded));
                    return;
                }
            } catch (error: unknown) {
                // If this is the last attempt, show error
                if (attempt === TOTAL_RETRIES) {
                    showErrorSheet();
                    return;
                }
            }
        }
    }, [showErrorSheet]);

    useEffect(() => {
        isMountedRef.current = true;

        if (geocomplyClientReady) {
            fetchGeotokenWithRetry();
        }

        // Cleanup function to mark component as unmounted
        return () => {
            isMountedRef.current = false;
            logger.debug(LOG_TAG, 'Component unmounting - cancelling geotoken prefetch');
        };
    }, [fetchGeotokenWithRetry, geocomplyClientReady]);
};
